import Cookie from 'js-cookie';
import moment from 'moment';
import { G2S } from '../g2s';
import * as flights from '../flights';
import * as api from '../api';

// eslint-disable-next-line @typescript-eslint/class-name-casing
interface g2sFFDCookiePassenger {
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    birthday: string | null;
    gender: string | null;
    citizenship: number | null;
    documentNumber: string | null;
    documentValidTill: string | null;
    documentValidTillEnabled: boolean | null;
}

// eslint-disable-next-line @typescript-eslint/class-name-casing
interface g2sFFDCookie {
    email: string | null;
    phone: string | null;
    passengers: (g2sFFDCookiePassenger | null)[];
}

export class Cookies {
    static gdprGet(): boolean {
        const cookie = Cookie.get('g2sGDPR');

        if (cookie && cookie == '1') {
            return true;
        }

        return false;
    }

    static gdprSet() {
        Cookie.set(
            'g2sGDPR',
            '1',
            {
                path: '/',
                expires: moment().add(2, 'years').toDate(),
                domain: G2S.config.cookieDomain,
                sameSite: G2S.isSecure ? 'none' : undefined, 
                secure: G2S.isSecure
            });
    }

    static getGaClientId(): string {
        const cookie = Cookie.get('_ga');

        if (cookie) {
            const chanks = cookie.split('.');

            if (chanks.Length > 3) {
                return chanks[2] + '.' + chanks[3];
            }
        }

        return '';
    }

    static getMarkerOwner(): string {
        const c = Cookies.getPartnerCookie();
        let result = ''; // Empty string is default value = direct traffic

        if (c) {
            c.split('&').forEach(function(item) {
                if (item.indexOf('mo=') === 0) {
                    result = item.substring(3);
                }
            });
        }

        return result;
    }

    static getMarkerValue(): string {
        const c = Cookies.getPartnerCookie();
        let result = '';

        if (c) {
            c.split('&').forEach(function(item) {
                if (item.indexOf('mv=') === 0) {
                    result = item.substring(3);
                }
            });
        }

        return result;
    }

    static setMarkerValue(markerValue: string) {
        const c = Cookies.getPartnerCookie();

        if (c && markerValue) {
            let result = '';

            c.split('&').forEach(function(item) {
                if (result) {
                    result += '&';
                }

                if (item.indexOf('mv=') === 0) {
                    result += 'mv=' + markerValue;
                } else {
                    result += item;
                }
            });

            // Custom converter to pass russian letters
            // Default converter ignores cookie with russian letters
            Cookie.withConverter({
                write: function (value, name) {
                    return encodeURIComponent(value)
                }
            }).set(
                'g2sp',
                result,
                {
                    domain: G2S.config.cookieDomain,
                    expires: moment().add(30, 'days').toDate(),
                    sameSite: G2S.isSecure ? 'none' : undefined,
                    secure: G2S.isSecure
                });
        }
    }

    static getPartnerCookie(): string {
        // Custom converter to pass russian letters
        // Default converter ignores cookie with russian letters
        let c: string = Cookie.withConverter({
            read: function (value, name) {
                return decodeURIComponent(value);
            }
        }).get('g2sp');

        // Use site/theme marker if cookie empty
        if (!c && G2S.config.themeMarker && G2S.config.themeMarker > 0) {
            c = 'mo=' + G2S.config.themeMarker;
        }

        return c ? c.toString() : '';
    }

    static removePartnerCookie() {
        Cookie.set(
            'g2sp',
            '',
            {
                path: '/',
                expires: moment().add(30, 'days').toDate(),
                domain: G2S.config.cookieDomain,
                sameSite: G2S.isSecure ? 'none' : undefined, 
                secure: G2S.isSecure
            });
    }

    static partnerCookieStringToObject(cookieString: string) {
        const result = {};

        if (cookieString && cookieString.length) {
            cookieString.split('&').forEach((keyValue) => {
                const keyAndVlue = keyValue.split('=');

                result[keyAndVlue[0]] = keyAndVlue[1];
            });
        }

        return result;
    }

    static partnerCookieObjectToString(cookieObject: object) {
        const result: string[] = [];

        for (const key in cookieObject) {
            result.push(`${key}=${cookieObject[key]}`);
        }

        return result.join('&');
    }

    static getSkyScannerRedirectId(): string {
        return Cookie.get('skyscanner_redirectid');
    }

    static removeSkyScannerRedirectId(): string {
        return Cookie.remove('skyscanner_redirectid');
    }

    static getOrderOneTimeCookie(): string {
        return Cookie.get('g2sOOT');
    }

    static setOrderOneTimeCookie(cookieData: string) {
        Cookie.set(
            'g2sOOT',
            cookieData,
            {
                path: '/',
                expires: moment().add(30, 'days').toDate(),
                domain: G2S.config.cookieDomain,
                sameSite: G2S.isSecure ? 'none' : undefined, 
                secure: G2S.isSecure
            });
    }

    static g2sFFDGet() {
        const g2sFFD = Cookie.get('g2sFFD');

        if (g2sFFD) {
            try {
                return JSON.parse(g2sFFD) as g2sFFDCookie;
            } catch (err) {
                console.log(err);
            }
        }

        return undefined;
    }

    // static setUserLanguage(cookieData: string){
    //     Cookie.set(
    //         'userLanguage',
    //         JSON.stringify(cookieData),
    //         {
    //             path: '/',
    //             domain: G2S.config.cookieDomain,
    //         });
    // }

    // static getUserLanguage() {
    //     return JSON.parse(Cookie.get('userLanguage'));
    // }

    static setIsUserCancelSelectionLanguage(cookieData: string){
        Cookie.set(
            'UserCancelSelectionLanguage',
            JSON.stringify(cookieData),
            {
                path: '/',
                domain: G2S.config.cookieDomain
            });
    }

    static getIsUserCancelSelectionLanguage() {
        return JSON.parse(Cookie.get('UserCancelSelectionLanguage'));
    }

    static g2sFFDSet(cookieData: g2sFFDCookie) {
        Cookie.set(
            'g2sFFD',
            JSON.stringify(cookieData),
            {
                path: '/',
                expires: moment().add(G2S.isDev ? 600 : 30, 'minutes').toDate(),
                domain: G2S.config.cookieDomain,
                sameSite: G2S.isSecure ? 'none' : undefined,
                secure: G2S.isSecure
            });
    }

    static g2sFFDUpsert(upsertData: {
        email?: string | null;
        phone?: string | null;
        passengers?: (flights.PassengerModel | null)[];
    }) {
        let g2sFFD = this.g2sFFDGet();

        if (!g2sFFD) {
            g2sFFD = {
                email: null,
                phone: null,
                passengers: []
            };
        }

        if (upsertData.email) {
            g2sFFD.email = upsertData.email;
        }
        if (upsertData.phone) {
            g2sFFD.phone = upsertData.phone;
        }
        if (upsertData.passengers && upsertData.passengers.length) {
            if (!g2sFFD.passengers) {
                g2sFFD.passengers = [];
            }

            const passengersDiff = upsertData.passengers.length - g2sFFD.passengers.length;

            if (passengersDiff > 0) {
                for (let i = 0; i < passengersDiff; i++) {
                    g2sFFD.passengers.push(null);
                }
            } else if (passengersDiff < 0) {
                for (let i = 0; i > passengersDiff; i--) {
                    g2sFFD.passengers.pop();
                }
            }
            upsertData.passengers.forEach((passenger, passengerIndex) => {
                if (passenger) {
                    const passengerToUpsert: g2sFFDCookiePassenger = {
                        firstName: null,
                        middleName: null,
                        lastName: null,
                        birthday: null,
                        gender: null,
                        citizenship: null,
                        documentNumber: null,
                        documentValidTill: null,
                        documentValidTillEnabled: null
                    };

                    passengerToUpsert.firstName = passenger.firstName;
                    if (!passenger.documentValidTillVisible) {
                        passengerToUpsert.middleName = passenger.middleName;
                    }
                    passengerToUpsert.lastName = passenger.lastName;
                    passengerToUpsert.birthday = passenger.birthday;
                    passengerToUpsert.gender = passenger.gender;
                    passengerToUpsert.citizenship = passenger.citizenship;
                    passengerToUpsert.documentNumber = passenger.documentNumber;
                    passengerToUpsert.documentValidTill = passenger.documentValidTill
                        ? moment(passenger.documentValidTill).format('YYYY-MM-DD')
                        : null;
                    if (!passenger.documentValidTillRequired) {
                        passengerToUpsert.documentValidTillEnabled = passenger.documentValidTillEnabled;
                    }

                    g2sFFD!.passengers![passengerIndex] = passengerToUpsert;
                }
            });
        }
        this.g2sFFDSet(g2sFFD);
    }

    static restoreReservationModelCookie(reservation: flights.ReservationModel) {
        if (!reservation) {
            return;
        }

        const cd = Cookie.get('g2sFFD');

        if (cd) {
            let cookieData: any = null;

            try {
                cookieData = JSON.parse(cd);
            } catch (e) {
                console.log(e);
            }

            if (cookieData) {
                if (cookieData.email) {
                    reservation.contacts.email = cookieData.email;
                }
                if (cookieData.phone) {
                    reservation.contacts.phone = cookieData.phone;
                }
                if (cookieData.passengers && cookieData.passengers.length) {
                    cookieData.passengers.forEach((p, index) => {
                        if (p && index < reservation.passengers.length) {
                            const passenger = reservation.passengers[index];

                            passenger.firstName = p.firstName;
                            // if (!passenger.validationConstraints.documentValidTillVisible) {
                            passenger.middleName = p.middleName;
                            // }
                            passenger.lastName = p.lastName;
                            passenger.birthday = p.birthday;
                            passenger.gender = p.gender;
                            passenger.citizenship = p.citizenship;
                            passenger.documentNumber = p.documentNumber;
                            passenger.documentValidTill = p.documentValidTill ? moment(p.documentValidTill).format('YYYY-MM-DD') : '';
                            // if (!passenger.validationConstraints.documentValidTillRequired) {
                            passenger.documentValidTillEnabled = p.documentValidTillEnabled;
                            // }
                        }
                    });
                }
            }
        }
    }

    static saveReservationModelCookie(reservation: flights.ReservationModel) {
        if (!reservation) {
            return;
        }

        const cookieData: g2sFFDCookie = {
            email: null,
            phone: null,
            passengers: []
        };

        // Load existed cookie as well as this method can be called on passengers step before contact details.
        const cd = Cookie.get('g2sFFD');
        if (cd) {
            let oldCookieData: g2sFFDCookie | null = null;

            try {
                oldCookieData = JSON.parse(cd) as g2sFFDCookie;
            } catch (e) {
                console.log(e);
            }

            if (oldCookieData) {
                if (oldCookieData.email) {
                    cookieData.email = oldCookieData.email;
                }
                if (oldCookieData.phone) {
                    cookieData.phone = oldCookieData.phone;
                }
            }
        }

        if (reservation.contacts.email) {
            cookieData.email = reservation.contacts.email;
        }

        if (reservation.contacts.phone) {
            cookieData.phone = reservation.contacts.phone;
        }

        reservation.passengers.forEach(p => {
            cookieData.passengers.push({
                firstName: p.firstName,
                middleName: p.middleName,
                lastName: p.lastName,
                birthday: p.birthday,
                gender: p.gender,
                citizenship: p.citizenship,
                documentNumber: p.documentNumber,
                documentValidTill: p.documentValidTill,
                documentValidTillEnabled: p.documentValidTillEnabled
            });
        });

        Cookie.set(
            'g2sFFD',
            JSON.stringify(cookieData),
            {
                path: '/',
                expires: moment().add(G2S.isDev ? 600 : 30, 'minutes').toDate(),
                domain: G2S.config.cookieDomain,
                sameSite: G2S.isSecure ? 'none' : undefined,
                secure: G2S.isSecure
            });
    }

    static g2scpnGet(): string {
        return Cookie.get('g2scpn');
    }
    static uidGet(): string {
        return Cookie.get('uid');
    }
    static g2scpnSet(couponName: string) {
        Cookie.set('g2scpn', couponName, {
            path: '/',
            expires: moment().add(1, 'days').toDate(),
            domain: G2S.config.cookieDomain,
            sameSite: G2S.isSecure ? 'none' : undefined,
            secure: G2S.isSecure
        });
    }
    static g2scpnDelete() {
        Cookie.set('g2scpn', '', {
            path: '/',
            expires: moment().add(G2S.isDev ? 600 : 30, 'minutes').toDate(),
            domain: G2S.config.cookieDomain,
            sameSite: G2S.isSecure ? 'none' : undefined,
            secure: G2S.isSecure
        });
    }

    static omitPromoPopupGet(value: string) {
        return Cookie.get(`g2sOmitPromoPopup_${value}`);
    }

    static omitPromoPopupSet(value: string, expires: string) {
        Cookie.set(
            `g2sOmitPromoPopup_${value}`,
            'true',
            {
                path: '/',
                expires: moment(expires, 'DD.MM.YYYY').toDate(),
                domain: G2S.config.cookieDomain,
                sameSite: G2S.isSecure ? 'none' : undefined,
                secure: G2S.isSecure
            });
    }

    static getMarkerReferrer() {
        const markerReferrer = Cookie.get('g2sMR');

        if (markerReferrer) {
            return markerReferrer;
        }

        return null;
    }

    static getSplitTest() {
        const splitVersion = Cookie.get('split_test_version');
        
        if (!G2S.config.instance) {
            G2S.config.instance = splitVersion;
            if (!splitVersion) {
                G2S.config.instance = 'frontend1';
            }
        }
    }

    static getChoiceType() {
        const ct = parseInt(Cookie.get('ct'));
        return isNaN(ct) ? null : <api.ProductChoiceType>ct;
    }

    static setChoiceType(ct: api.ProductChoiceType) {
        Cookie.set('ct', ct, {
            path: '/',
            expires: moment().add(1, 'days').toDate(),
            domain: G2S.config.cookieDomain,
            sameSite: G2S.isSecure ? 'none' : undefined,
            secure: G2S.isSecure
        });
    }
}
